import { FC, PropsWithChildren, ReactNode, RefObject, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { Card } from "./Card";

export interface DropdownProps {
  // eslint-disable-next-line
  readonly refToFocus?: RefObject<any>;
  readonly label: ReactNode;
  readonly isOpen?: boolean;
  readonly onBlur?: () => void;
  readonly onClick?: () => void;
  readonly keepTitleFocusedOnMenuClose?: boolean;
}

const DropdownContainer = styled.div`
  cursor: pointer;
  position: relative;
  display: inline-block;
  width: 100%;
`;

const DropdownContentContainer = styled.div`
  position: absolute;
  display: block;
  z-index: 2;
  height: unset !important;
  width: 100%;

  div {
    padding: 4px;
  }
`;

const LabelButton = styled.div`
  display: inline-block;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

export const Dropdown: FC<PropsWithChildren<DropdownProps>> = ({
  refToFocus,
  label,
  isOpen,
  onBlur,
  onClick,
  children,
  keepTitleFocusedOnMenuClose = false,
}) => {
  const menuRef = useRef<HTMLDivElement>();

  const onTitleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onClick && onClick();
    },
    [onClick],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onClick && onClick();
    },
    [onClick],
  );

  const onContentBlur = useCallback(
    (e: React.FocusEvent) => {
      e.preventDefault();
      if (isOpen) {
        onBlur && onBlur();
      }
    },
    [onBlur, isOpen],
  );

  useEffect(() => {
    if (isOpen) {
      if (refToFocus) {
        refToFocus?.current?.focus();
      } else {
        menuRef?.current?.focus();
      }
    }
    if (keepTitleFocusedOnMenuClose) {
      if (refToFocus) {
        refToFocus?.current?.focus();
      } else {
        menuRef?.current?.focus();
      }
    }
  }, [isOpen, refToFocus]);

  return (
    <DropdownContainer onBlur={onContentBlur}>
      {isOpen && <LabelButton onMouseDown={onMouseDown}>{label}</LabelButton>}
      {!isOpen && <LabelButton onClick={onTitleClick}>{label}</LabelButton>}
      {isOpen && (
        <DropdownContentContainer ref={menuRef} tabIndex={0}>
          <Card>{children}</Card>
        </DropdownContentContainer>
      )}
    </DropdownContainer>
  );
};
